import { Layout, Menu, Button, Avatar, Tag, ConfigProvider } from 'antd'
import { LogoutOutlined, UserOutlined, DatabaseOutlined, WhatsAppOutlined, HistoryOutlined, FileTextOutlined, TeamOutlined, EditOutlined, LockOutlined, FilePdfOutlined, BarChartOutlined, ShoppingCartOutlined, GlobalOutlined, AuditOutlined, FileSearchOutlined, ThunderboltOutlined, SolutionOutlined, CarOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import dayjs from 'dayjs'
import arEG from 'antd/locale/ar_EG'
import enUS from 'antd/locale/en_US'
import '../i18n/i18n'
import ERPSettings from './ERPSettings'
import Bills from './Bills'
import Accounts from './Accounts'
import WhatsApp from './WhatsApp'
import MessageTemplateDesigner from './MessageTemplateDesigner'
import ChangePassword from './ChangePassword'
import PdfTemplateUpload from './PdfTemplateUpload'
import DefaultPdfDesignSettings from './DefaultPdfDesignSettings'
import AutoSendSettings from './AutoSendSettings'
import Sales from './Sales'
import Purchases from './Purchases'
import SendLogs from './SendLogs'
import HRDashboard from './HRDashboard'
import TransportDashboard from './TransportDashboard'
import IncomeStatementSettings from './IncomeStatementSettings'
import IncomeStatementReport from './IncomeStatementReport'

const { Sider, Content, Header } = Layout

function Dashboard({ user, onLogout }) {
  const { t, i18n } = useTranslation()
  const whatsappEnabled = user?.whatsapp_enabled ?? true
  const dashboardEnabled = user?.dashboard_enabled ?? false
  const incomeStatementEnabled = user?.income_statement_enabled ?? false

  // اختيار اللغة، بنفس فلسفة اختيار القاعدة لكل جهاز على حدة —
  // كل متصفح/جهاز يحتفظ باختياره الخاص عبر localStorage، ولا يوجد
  // ربط بحساب المستخدم نفسه، لتفادي تعقيد إضافي في PostgreSQL الآن
  const [language, setLanguage] = useState(localStorage.getItem('app_language') || 'ar')

  useEffect(() => {
    i18n.changeLanguage(language)
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = language
    dayjs.locale(language === 'ar' ? 'ar' : 'en')
    localStorage.setItem('app_language', language)
  }, [language, i18n])

  const toggleLanguage = () => {
    setLanguage(prev => (prev === 'ar' ? 'en' : 'ar'))
  }

  const whatsappItems = [
    { key: 'bills',     icon: <FileTextOutlined />,  label: t('menu.bills')        },
    // { key: 'accounts', icon: <TeamOutlined />, label: t('menu.accounts') }, // أُخفيت مؤقتاً بطلب العميل — الصفحة والمسار ما زالا موجودين، فقط الرابط مخفي من القائمة
    { key: 'whatsapp',  icon: <WhatsAppOutlined />,   label: t('menu.whatsapp')    },
    { key: 'template',  icon: <EditOutlined />,       label: t('menu.template')    },
    // { key: 'pdf-template', icon: <FilePdfOutlined />, label: t('menu.pdfTemplate') }, // أُخفيت مؤقتاً بطلب العميل — الصفحة والمسار ما زالا موجودين، فقط الرابط مخفي من القائمة
    { key: 'pdf-design', icon: <FilePdfOutlined />, label: t('menu.pdfDesign') },
    { key: 'auto-send-settings', icon: <ThunderboltOutlined />, label: t('menu.autoSendSettings') },
    { key: 'logs',      icon: <HistoryOutlined />,    label: t('menu.logs')        },
  ]

  // عناصر "الداشبورد" — تقارير وإحصائيات
  const dashboardItems = [
    { key: 'sales', icon: <BarChartOutlined />, label: t('menu.sales') },
    { key: 'purchases', icon: <ShoppingCartOutlined />, label: t('menu.purchases') },
    { key: 'hr-dashboard', icon: <SolutionOutlined />, label: t('menu.hrDashboard') },
    { key: 'transport-dashboard', icon: <CarOutlined />, label: t('menu.transportDashboard') },
  ]

  // ميزة مستقلة بذاتها (income_statement_enabled)، وليست جزءاً من
  // dashboardItems، لأن عميلاً قد يفعّل قائمة الدخل بدون الداشبورد
  // العادي (مبيعات/مشتريات) والعكس صحيح تماماً
  const incomeStatementItems = [
    { key: 'income-statement-report', icon: <FileSearchOutlined />, label: t('menu.incomeStatement') },
    { key: 'income-statement-settings', icon: <AuditOutlined />, label: t('menu.incomeStatementSettings') },
  ]

  const sharedItems = [
    { key: 'erp',       icon: <DatabaseOutlined />,   label: t('menu.erp')      },
    { key: 'password',  icon: <LockOutlined />,       label: t('menu.password') },
  ]

  // القائمة النهائية المعروضة: عناصر واتساب فقط إن كانت مفعّلة،
  // عناصر الداشبورد فقط إن كانت مفعّلة، والمشتركة تظهر دائماً
  const items = [
    ...(whatsappEnabled ? whatsappItems : []),
    ...(dashboardEnabled ? dashboardItems : []),
    ...(incomeStatementEnabled ? incomeStatementItems : []),
    ...sharedItems
  ]

  // أول صفحة افتراضية عند الدخول: أول صفحة متاحة فعلياً لهذا العميل
  const [current, setCurrent] = useState(items[0]?.key || 'erp')
  const [dbCaption, setDbCaption] = useState(null)
  const token = localStorage.getItem('token')

  // عند فتح اللوحة: نهيّئ اختيار القاعدة الخاص بهذا الجهاز.
  // إذا كان هذا الجهاز اختار قاعدة من قبل (localStorage) نستخدمها
  // ونعيد إرسالها كـ Header مع كل الطلبات القادمة، وإلا نرث القيمة
  // الافتراضية العامة الحالية وقت أول زيارة لهذا الجهاز فقط —
  // ومن هذه اللحظة يصبح هذا الجهاز مستقلاً بخياره عن الأجهزة الأخرى.
  useEffect(() => {
    const initDeviceDb = async () => {
      try {
        const res = await axios.get('/api/settings/erp', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const localDb = localStorage.getItem('device_selected_db')
        const dbToUse = localDb || res.data.selected_db
        if (dbToUse) {
          axios.defaults.headers.common['X-Selected-Db'] = dbToUse
          if (!localDb) localStorage.setItem('device_selected_db', dbToUse)
        }
        const captions = res.data.database_captions || {}
        setDbCaption(captions[dbToUse] || dbToUse || null)
      } catch (err) {
        // فشل جلب الإعدادات هنا ليس خطأً حرجاً — الصفحات الأخرى
        // (الفواتير، إعدادات ERP) ستعرض رسائل الخطأ المناسبة بنفسها
      }
    }
    initDeviceDb()
  }, [token])

  return (
    <ConfigProvider direction={language === 'ar' ? 'rtl' : 'ltr'} locale={language === 'ar' ? arEG : enUS}>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider theme="dark">
          <div style={{
            color: 'white',
            textAlign: 'center',
            padding: '16px',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            {t('common.appTitle')}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[current]}
            items={items}
            onClick={e => setCurrent(e.key)}
          />
        </Sider>

        <Layout>
          <Header style={{
            background: '#fff',
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 'bold' }}>
                {t('common.systemTitle')}
              </span>
              {dbCaption && (
                <Tag icon={<DatabaseOutlined />} color="blue">
                  {dbCaption}
                </Tag>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button icon={<GlobalOutlined />} onClick={toggleLanguage}>
                {t('language.switchTo')}
              </Button>
              <Avatar icon={<UserOutlined />} />
              <span>{user.name}</span>
              <Button icon={<LogoutOutlined />} onClick={onLogout} danger>
                {t('common.logout')}
              </Button>
            </div>
          </Header>

          <Content style={{ margin: 24 }}>
            {current === 'bills'    && whatsappEnabled && <Bills />}
            {current === 'accounts' && whatsappEnabled && <Accounts />}
            {current === 'erp'      && <ERPSettings />}
            {current === 'whatsapp' && whatsappEnabled && <WhatsApp user={user} />}
            {current === 'template' && whatsappEnabled && <MessageTemplateDesigner token={token} templateType="bill" />}
            {current === 'pdf-template' && whatsappEnabled && <PdfTemplateUpload />}
            {current === 'pdf-design' && whatsappEnabled && <DefaultPdfDesignSettings />}
            {current === 'auto-send-settings' && whatsappEnabled && <AutoSendSettings />}
            {current === 'logs'     && whatsappEnabled && <SendLogs />}
            {current === 'password' && <ChangePassword />}
            {current === 'sales'    && dashboardEnabled && <Sales />}
            {current === 'purchases' && dashboardEnabled && <Purchases />}
            {current === 'hr-dashboard' && dashboardEnabled && <HRDashboard />}
            {current === 'transport-dashboard' && dashboardEnabled && <TransportDashboard />}
            {current === 'income-statement-report' && incomeStatementEnabled && <IncomeStatementReport />}
            {current === 'income-statement-settings' && incomeStatementEnabled && <IncomeStatementSettings />}
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  )
}

export default Dashboard
